/**
 * Timer keys, ranges and labels (design doc §14.3), without the zod schema, so the web client can
 * use them without bundling zod (see catalog.ts). All durations are in whole seconds.
 */
import { GRAND_COUNCIL_MIN_PLAYERS, type Range } from "./setup-table.ts";

export const TIMER_KEYS = [
  "arrival",
  "mingle",
  "cardChoice",
  "duskPacing",
  "discussion",
  "nominations",
  "defencePerNominee",
  "ballot",
] as const;
export type TimerKey = (typeof TIMER_KEYS)[number];

/** Allowed range for each timer, in seconds (§14.3). */
export const TIMER_RANGES: Readonly<Record<TimerKey, Range>> = {
  arrival: { min: 30, max: 300 },
  mingle: { min: 120, max: 900 },
  cardChoice: { min: 8, max: 30 },
  duskPacing: { min: 20, max: 120 },
  discussion: { min: 60, max: 900 },
  nominations: { min: 30, max: 180 },
  defencePerNominee: { min: 10, max: 60 },
  ballot: { min: 15, max: 90 },
};

export const TIMER_LABELS: Readonly<Record<TimerKey, string>> = {
  arrival: "Arrival",
  mingle: "Mingle",
  cardChoice: "Card choice in a handshake",
  duskPacing: "Dusk reveal pacing",
  discussion: "Council discussion",
  nominations: "Nomination window",
  defencePerNominee: "Defence, per nominee",
  ballot: "Ballot",
};

/** The remaining time of a running phase can never be cut below this (§14.3). */
export const MIN_REMAINING_SECONDS = 15;

/** Formats seconds as m:ss, as in the design doc (e.g. 90 → "1:30"). */
export function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Every timer's length in seconds (the shape of TimerConfig, without its zod schema). */
export type TimerValues = Record<TimerKey, number>;

/** Standard timers for a player count: Mingle and discussion are 6:00 at 15+ players. */
export function defaultTimers(playerCount: number): TimerValues {
  const large = playerCount >= GRAND_COUNCIL_MIN_PLAYERS;
  return {
    arrival: 120,
    mingle: large ? 360 : 300,
    cardChoice: 15,
    duskPacing: 60,
    discussion: large ? 360 : 240,
    nominations: 60,
    defencePerNominee: 20,
    ballot: 30,
  };
}

export const TIMER_PRESET_IDS = ["quick", "standard", "relaxed", "large-group"] as const;
export type TimerPresetId = (typeof TIMER_PRESET_IDS)[number];

export const TIMER_PRESET_LABELS: Readonly<Record<TimerPresetId, string>> = {
  quick: "Quick",
  standard: "Standard",
  relaxed: "Relaxed (+50 %)",
  "large-group": "Large group",
};

function clampTimer(key: TimerKey, seconds: number): number {
  const { min, max } = TIMER_RANGES[key];
  return Math.min(max, Math.max(min, seconds));
}

/** Scales every timer, rounds to 5 s (1 s for card choice) and clamps into the allowed range. */
function scaleTimers(base: TimerValues, factor: number): TimerValues {
  const out = { ...base };
  for (const key of TIMER_KEYS) {
    const step = key === "cardChoice" ? 1 : 5;
    out[key] = clampTimer(key, Math.round((base[key] * factor) / step) * step);
  }
  return out;
}

/**
 * Timer presets (§14.3). The design doc defines only Standard (the defaults) and Relaxed (+50 %).
 * Quick and Large group are this package's calibration and should be tuned from telemetry:
 * - Quick: tighter phases for groups that already know the game.
 * - Large group: extra time for crowded rooms, on top of the 15+ defaults.
 */
export function timerPreset(id: TimerPresetId, playerCount: number): TimerValues {
  const standard = defaultTimers(playerCount);
  switch (id) {
    case "standard":
      return standard;
    case "relaxed":
      return scaleTimers(standard, 1.5);
    case "quick":
      return {
        arrival: 60,
        mingle: playerCount >= GRAND_COUNCIL_MIN_PLAYERS ? 300 : 240,
        cardChoice: 12,
        duskPacing: 30,
        discussion: playerCount >= GRAND_COUNCIL_MIN_PLAYERS ? 300 : 180,
        nominations: 45,
        defencePerNominee: 15,
        ballot: 20,
      };
    case "large-group":
      return {
        arrival: 180,
        mingle: 420,
        cardChoice: 15,
        duskPacing: 90,
        discussion: 420,
        nominations: 90,
        defencePerNominee: 20,
        ballot: 45,
      };
  }
}
